import { BiddingBidSendErrorCategory } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import { Tooltip } from '@enechain/ecloud-designsystem'

/**
 * 送信失敗理由カテゴリ → 見出し i18n key。
 * 分類そのもの (last_error → category) は backend の ClassifyBidSendError が担い、
 * frontend はここで category → 表示ラベルの写像のみ行う。
 */
const SEND_ERROR_CATEGORY_LABEL_KEY: Record<
  BiddingBidSendErrorCategory,
  string
> = {
  [BiddingBidSendErrorCategory.UNSPECIFIED]:
    'page:biddingIntraday.sendErrorCategory.unspecified',
  [BiddingBidSendErrorCategory.DELIVERY_CONTRACT_CODE_MISSING]:
    'page:biddingIntraday.sendErrorCategory.deliveryContractCodeMissing',
  [BiddingBidSendErrorCategory.GATE_CLOSED]:
    'page:biddingIntraday.sendErrorCategory.gateClosed',
  [BiddingBidSendErrorCategory.VALIDATION_REJECTED]:
    'page:biddingIntraday.sendErrorCategory.validationRejected',
  [BiddingBidSendErrorCategory.TRANSIENT]:
    'page:biddingIntraday.sendErrorCategory.transient',
  [BiddingBidSendErrorCategory.TARGET_NOT_FOUND]:
    'page:biddingIntraday.sendErrorCategory.targetNotFound',
  [BiddingBidSendErrorCategory.OTHER]:
    'page:biddingIntraday.sendErrorCategory.other',
  [BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION]:
    'page:biddingIntraday.sendErrorCategory.internalValidationViolation',
  [BiddingBidSendErrorCategory.INTERNAL_VALIDATION_VIOLATION_APPROVAL_MODE]:
    'page:biddingIntraday.sendErrorCategory.internalValidationViolation',
}

/**
 * 送信失敗の理由 (カテゴリ見出し + 生 errorDetail) を hover で見せる ecloud Tooltip。
 * errorCategory が UNSPECIFIED / 未供給なら children をそのまま返す。
 * スポット・ブロック・時間前の一覧のステータス列のバッジに理由を添える (AC-BIN-059 /
 * AC-BIN-035 / AC-BSP-011)。
 *
 * `gateClosedNotice` を渡すと、 理由が締切超過 (GATE_CLOSED) の時だけ見出しと生
 * errorDetail (内部理由) の代わりにこの利用者向け文言 (+ 再利用の案内) を出す。
 * スポット入札が AC-BSP-011 の「入札締切を超過したため送信できませんでした」 と
 * 翌営業日向けの再利用案内を出すための口で、 時間前入札など渡さない画面は従来どおり。
 */
export const SendErrorReasonTooltip = ({
  errorCategory,
  errorDetail,
  gateClosedNotice,
  children,
}: {
  errorCategory?: BiddingBidSendErrorCategory
  errorDetail?: string
  gateClosedNotice?: { message: string; hint?: string }
  children: React.ReactElement
}): React.ReactElement => {
  const { t } = useTranslation()
  if (
    errorCategory === undefined ||
    errorCategory === BiddingBidSendErrorCategory.UNSPECIFIED
  ) {
    return children
  }

  const showGateClosedNotice =
    gateClosedNotice !== undefined &&
    errorCategory === BiddingBidSendErrorCategory.GATE_CLOSED

  return (
    <Tooltip
      content={
        showGateClosedNotice ? (
          <div>
            <div>{gateClosedNotice.message}</div>
            {gateClosedNotice.hint !== undefined && (
              <div>{gateClosedNotice.hint}</div>
            )}
          </div>
        ) : (
          <div>
            {/* 見出し (= カテゴリ i18n ラベル) と 生 detail (= last_error、
              内部に改行を含みうるため whiteSpace: pre-line) を別要素で分け、
              getByText 等のテスト assertion が単一要素の textContent で
              ヒットできるようにする (= 1 要素に複数テキストノードを詰めない)。 */}
            <div>{t(SEND_ERROR_CATEGORY_LABEL_KEY[errorCategory])}</div>
            {errorDetail !== undefined && errorDetail !== '' && (
              <div style={{ whiteSpace: 'pre-line' }}>{errorDetail}</div>
            )}
          </div>
        )
      }
    >
      {/* Tooltip は asChild で children に trigger props (onPointerEnter 等) を
          clone するが、 バッジは追加 props を
          forward しないため、 素の <span> でラップして clone 先を DOM 要素にする
          (= clone した props がそのまま DOM に載る)。 */}
      <span>{children}</span>
    </Tooltip>
  )
}
